import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import {
  GAReportedTableHeadCells,
  GeneralAdminRoutes,
  getComparator,
  stableSort,
} from "../../../../../constants";
import { TableRowSection } from "../components";
import TablePaginationSection from "../../../../../common/TablePaginationSection";
import { useContentModerationContext } from "../../../../../providers/GeneralAdmin/ContentModerationProvider";
import CommonTableHead from "../../../../../common/CommonTableHead";

export default function ContentModerationTable({
  selected,
  setSelected,
  contentReportsData,
  userDetailsPageReports,
  detailsPageReports,
  hideCheckbox = false,
}) {
  const {
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
  } = useContentModerationContext();
  const scrollbarsRef = useRef(null);
  const navigate = useNavigate();

  // Initialize column widths based on GAReportedTableHeadCells
  const initializeColumnWidths = () => {
    const widths = {};
    GAReportedTableHeadCells.forEach(cell => {
      widths[cell.id] = cell.width || 150;
    });
    return widths;
  };

  const [columnWidths, setColumnWidths] = useState(initializeColumnWidths);

  const handleColumnResize = (columnId, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnId]: newWidth
    }));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page - 1 > 0
      ? Math.max(0, (1 + page - 1) * rowsPerPage - contentReportsData.length)
      : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(contentReportsData, getComparator(order, orderBy)).slice(
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, contentReportsData]
  );

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = visibleRows.map((n) => n.id);
      // const newSelected = contentReportsData.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClickRow = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    console.log(newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // console.log(Math.ceil(2.1))

  return (
    <Box sx={mainBoxContainerStyles}>
      <Paper sx={tablePaperContainerStyles}>
        <TableContainer
          style={{ border: "none", boxShadow: "none", maxHeight: "calc(90vh - 220px)", maxWidth: "calc(90vw - 120px)" }}
        >
          <Table
            stickyHeader
            //aria-label="sticky table"
            aria-labelledby="tableTitle"
          >
            <CommonTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={visibleRows.length}
              tableHeadCells={GAReportedTableHeadCells}
              columnWidths={columnWidths}
              onColumnResize={handleColumnResize}
              detailsPageReports={detailsPageReports && detailsPageReports}
              hideCheckbox={hideCheckbox}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;
                const numberType = index % 2 == 0;

                return (
                  <TableRowSection
                    isItemSelected={isItemSelected}
                    row={row}
                    handleClickRow={handleClickRow}
                    numberType={numberType}
                    labelId={labelId}
                    key={index}
                    userDetailsPageReports={userDetailsPageReports}
                    hideCheckbox={hideCheckbox}
                    columnWidths={columnWidths}
                    handleNavigateToDetails={() => {
                      // navigate(
                      //   GeneralAdminRoutes.contentModerationDetails + row.id
                      // );
                      navigate(GeneralAdminRoutes.contentModerationDetails, {
                        state: { reportDetails: row },
                      });
                    }}
                  />
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}

              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No results found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <TablePaginationSection
        count={Math.ceil(contentReportsData.length / rowsPerPage)}
        page={page}
        total={contentReportsData.length ?? 0}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}

const mainBoxContainerStyles = {
  width: "100%",
  paddingRight: 1,
  border: "none",
  boxShadow: "none",
  // marginTop: 2,
};

const tablePaperContainerStyles = {
  // width: "100%",
  // mb: 2,
  // border: "none",
  // boxShadow: "none",
  // height: "67vh",
  maxWidth: "100%",
  mb: 1,
  border: "none",
  boxShadow: "none",
  mt: 1,
  overflow: "hidden",
};
